import { SearchOutlined } from "@ant-design/icons";
import { Input } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    getOrderAmountListThunk,
    getUserTeamGetByManagerThunk,
} from "tm-shared/api";

import Header from "@/components/Header";
import Meta from "@/components/Meta";
import { PrimaryText, SecondaryText } from "@/components/Typography/Styled";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { setCurrentUser, setSearchAgent } from "@/store/slices/sv/sv.slice";

import {
    StyledScrollContent,
    StyledSVCInputWrapper,
    StyledSVCList,
    StyledSVCListItem,
    StyledSVCMainContainer,
} from "./Styled";
import { TotalAccordion } from "./TotalAccordion";

function SVCheckout() {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const {
        user: { id },
        sv: {
            team,
            checkoutAgentsList,
            checkoutTotals,
            searchAgent,
            lastUpdated,
        },
        offline: { offlineMode },
    } = useAppSelector((state) => state);

    const [svLoading, setSvLoading] = useState(false);

    const normalizedSearch = searchAgent.trim().toLowerCase();

    const filteredAgents = useMemo(() => {
        if (!normalizedSearch) return checkoutAgentsList ?? [];

        return (
            checkoutAgentsList?.filter(
                ({ userName }) =>
                    userName?.toLowerCase().includes(normalizedSearch),
            ) ?? []
        );
    }, [checkoutAgentsList, normalizedSearch]);

    const handleNavigateToCheckout = useCallback(
        async (agentId: string) => {
            dispatch(setCurrentUser(agentId));
            navigate(`/sv-checkout/${agentId}`);
        },
        [dispatch, navigate],
    );

    useEffect(() => {
        if (offlineMode) return;

        try {
            setSvLoading(true);
            if (team?.teamId) {
                dispatch(
                    getOrderAmountListThunk({ teamId: team.teamId }),
                ).unwrap();
                return;
            }

            dispatch(getUserTeamGetByManagerThunk({ managerId: id })).unwrap();
        } catch (error) {
            console.error(error);
        } finally {
            setSvLoading(false);
        }
    }, [dispatch, id, offlineMode, team?.teamId]);

    const isLoading = svLoading;

    return (
        <>
            <Meta title="Checkout" />
            <Header title="Єдиний кошик" titleLoading={isLoading} />

            <StyledSVCMainContainer>
                <TotalAccordion {...checkoutTotals} />

                <StyledScrollContent>
                    <StyledSVCInputWrapper>
                        <Input
                            size="large"
                            placeholder="Введіть ім'я"
                            value={searchAgent}
                            addonAfter={<SearchOutlined />}
                            onChange={(e) =>
                                dispatch(setSearchAgent(e.target.value))
                            }
                        />
                    </StyledSVCInputWrapper>

                    {lastUpdated?.checkouts && (
                        <SecondaryText>
                            Останнє оновлення: {lastUpdated.checkouts}
                        </SecondaryText>
                    )}

                    <StyledSVCList>
                        {filteredAgents.map(
                            ({ userId, userName, amountTotal }) => (
                                <StyledSVCListItem
                                    key={userId}
                                    onClick={() =>
                                        handleNavigateToCheckout(userId)
                                    }
                                >
                                    <PrimaryText>{userName}</PrimaryText>
                                    <SecondaryText>
                                        {amountTotal.toLocaleString("uk-UA", {
                                            minimumFractionDigits: 2,
                                        })}{" "}
                                        грн.
                                    </SecondaryText>
                                </StyledSVCListItem>
                            ),
                        )}
                    </StyledSVCList>
                </StyledScrollContent>
            </StyledSVCMainContainer>
        </>
    );
}

export default SVCheckout;
